import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { formatApiError } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Building2, Plus, Search, Filter, Eye } from 'lucide-react';

const STATUS_LABELS = { active: 'Activo', suspended: 'Suspendido', archived: 'Archivado' };
const STATUS_COLORS = {
  active: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  suspended: 'bg-amber-50 text-amber-700 border-amber-200',
  archived: 'bg-slate-50 text-slate-600 border-slate-200',
};

export default function Tenants() {
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterVertical, setFilterVertical] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [showArchived, setShowArchived] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [verticals, setVerticals] = useState([]);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  // Create form
  const [form, setForm] = useState({
    name: '', slug: '', vertical: '', subtype: '', timezone: 'America/Mexico_City',
    country: 'Mexico', locale: 'es-MX', currency: 'MXN', plan_id: '',
    owner_email: '', owner_name: '', owner_password: '',
  });
  const [creating, setCreating] = useState(false);
  const [catalogPlans, setCatalogPlans] = useState([]);

  const fetchTenants = useCallback(async () => {
    try {
      const params = {};
      if (search) params.search = search;
      if (filterVertical && filterVertical !== 'all') params.vertical = filterVertical;
      if (filterStatus && filterStatus !== 'all') params.status = filterStatus;
      else if (showArchived) params.include_archived = true;
      const { data } = await api.get('/admin/tenants', { params });
      setTenants(data);
    } catch { } finally { setLoading(false); }
  }, [search, filterVertical, filterStatus, showArchived]);

  useEffect(() => { fetchTenants(); }, [fetchTenants]);
  useEffect(() => {
    api.get('/admin/verticals').then(r => setVerticals(r.data)).catch(() => {});
    api.get('/admin/plans/catalog?status=active').then(r => {
      const plans = r.data || [];
      setCatalogPlans(plans);
      const defaultPlan = plans.find(p => p.is_default) || plans[0];
      if (defaultPlan) setForm(f => ({ ...f, plan_id: defaultPlan.id }));
    }).catch(() => {});
  }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    setError('');
    setCreating(true);
    try {
      const { data } = await api.post('/admin/tenants', form);
      setShowCreate(false);
      setForm(f => ({ name: '', slug: '', vertical: '', timezone: 'America/Mexico_City', country: 'Mexico', locale: 'es-MX', currency: 'MXN', plan_id: f.plan_id, owner_email: '', owner_name: '', owner_password: '' }));
      fetchTenants();
      navigate(`/admin/tenants/${data.id}`);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally { setCreating(false); }
  };

  return (
    <div data-testid="tenants-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-[#0F172A] tracking-tight">Tenants</h1>
          <p className="text-sm text-[#64748B] mt-1">Gestión de clientes y organizaciones</p>
        </div>
        <Button
          onClick={() => setShowCreate(true)}
          className="bg-[#F72585] text-white hover:bg-[#D81B60] rounded-lg font-medium w-full sm:w-auto"
          data-testid="create-tenant-button"
        >
          <Plus className="w-4 h-4 mr-2" />
          Nuevo Tenant
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row sm:flex-wrap items-stretch sm:items-center gap-3 mb-6">
        <div className="relative flex-1 min-w-0 sm:min-w-[200px] sm:max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94A3B8]" />
          <Input
            placeholder="Buscar tenant..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 bg-white border-[#E2E8F0] rounded-lg text-sm"
            data-testid="search-tenants"
          />
        </div>
        <Select value={filterVertical} onValueChange={setFilterVertical}>
          <SelectTrigger className="w-full sm:w-[180px] bg-white border-[#E2E8F0] rounded-lg text-sm" data-testid="filter-vertical">
            <Filter className="w-3.5 h-3.5 mr-2 text-[#94A3B8]" />
            <SelectValue placeholder="Vertical" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas</SelectItem>
            {verticals.map(v => <SelectItem key={v.key} value={v.key}>{v.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filterStatus || 'all'} onValueChange={(v) => setFilterStatus(v === 'all' ? '' : v)}>
          <SelectTrigger className="w-full sm:w-[150px] bg-white border-[#E2E8F0] rounded-lg text-sm" data-testid="filter-status">
            <SelectValue placeholder="Estado" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Activos (sin archivados)</SelectItem>
            <SelectItem value="active">Solo activos</SelectItem>
            <SelectItem value="suspended">Suspendidos</SelectItem>
            <SelectItem value="archived">Solo archivados</SelectItem>
          </SelectContent>
        </Select>
        <label className="flex items-center gap-2 text-sm text-[#64748B] cursor-pointer select-none">
          <input
            type="checkbox"
            checked={showArchived}
            onChange={(e) => setShowArchived(e.target.checked)}
            className="rounded border-[#CBD5E1]"
            data-testid="include-archived-checkbox"
          />
          Incluir archivados en la lista
        </label>
      </div>
      <p className="text-xs text-[#94A3B8] -mt-4 mb-6">
        Por defecto los clientes archivados no aparecen aquí. Sus datos se conservan; solo salen de la operación diaria.
      </p>

      {/* Table */}
      <div className="bg-white border border-[#E2E8F0] rounded-xl overflow-hidden">
        {loading ? (
          <div className="flex justify-center py-16">
            <div className="w-8 h-8 border-2 border-[#E2E8F0] border-t-[#0F172A] rounded-full animate-spin" />
          </div>
        ) : tenants.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-[#64748B]">
            <Building2 className="w-12 h-12 mb-3 text-[#E2E8F0]" />
            <p className="text-sm font-medium">Sin tenants</p>
            <p className="text-xs mt-1">Crea tu primer tenant para comenzar</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="tenants-table">
              <thead>
                <tr className="bg-[#F8F9FA]">
                  <th className="text-left text-xs uppercase tracking-widest text-[#64748B] font-semibold px-4 py-3">Nombre</th>
                  <th className="text-left text-xs uppercase tracking-widest text-[#64748B] font-semibold px-4 py-3 hidden sm:table-cell">Vertical</th>
                  <th className="text-left text-xs uppercase tracking-widest text-[#64748B] font-semibold px-4 py-3 hidden md:table-cell">Plan</th>
                  <th className="text-left text-xs uppercase tracking-widest text-[#64748B] font-semibold px-4 py-3 hidden lg:table-cell">País</th>
                  <th className="text-left text-xs uppercase tracking-widest text-[#64748B] font-semibold px-4 py-3 hidden sm:table-cell">Usuarios</th>
                  <th className="text-left text-xs uppercase tracking-widest text-[#64748B] font-semibold px-4 py-3">Estado</th>
                  <th className="text-left text-xs uppercase tracking-widest text-[#64748B] font-semibold px-4 py-3">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {tenants.map(t => (
                  <tr key={t.id} className="border-t border-[#E2E8F0] hover:bg-[#F8F9FA] transition-colors" data-testid={`tenant-row-${t.id}`}>
                    <td className="px-4 py-3">
                      <div>
                        <p className="text-sm font-medium text-[#0F172A] flex items-center gap-1.5">
                          {t.name}
                          {/* S134.1 — cuenta de la marca propia. Se distingue a simple
                              vista porque NO es un cliente: no se le cobra, no comisiona
                              y no entra a la cartera. */}
                          {t.is_corp_account && (
                            <span
                              title="Cuenta de la marca: aquí hacemos demos y opera la casa. No se le cobra, no comisiona y no aparece en cartera."
                              className="text-[10px] px-1.5 py-0.5 rounded-full font-semibold bg-indigo-50 text-indigo-700 border border-indigo-200">
                              Corp
                            </span>
                          )}
                        </p>
                        <p className="text-xs text-[#64748B]">{t.slug}</p>
                        <p className="text-xs text-[#64748B] sm:hidden capitalize mt-0.5">{t.vertical?.replace(/_/g, ' ')}</p>
                      </div>
                    </td>
                    <td className="px-4 py-3 hidden sm:table-cell">
                      <span className="text-xs px-2 py-1 bg-[#F8F9FA] text-[#64748B] rounded-full border border-[#E2E8F0] capitalize">
                        {t.vertical?.replace(/_/g, ' ')}
                      </span>
                      {t.subtype && <span className="block text-[10px] text-[#94A3B8] mt-0.5">{t.subtype}</span>}
                    </td>
                    <td className="px-4 py-3 hidden md:table-cell text-sm text-[#64748B] capitalize">
                      {t.is_corp_account ? <span className="text-indigo-700">Ilimitado (Corp)</span> : t.plan}
                    </td>
                    <td className="px-4 py-3 hidden lg:table-cell text-sm text-[#64748B]">{t.country}</td>
                    <td className="px-4 py-3 hidden sm:table-cell text-sm text-[#0F172A] font-medium">{t.user_count}/{t.max_users}</td>
                    <td className="px-4 py-3">
                      <span className={`text-xs px-2 py-1 rounded-full font-medium border ${STATUS_COLORS[t.status] || STATUS_COLORS.active}`}>
                        {STATUS_LABELS[t.status] || t.status}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => navigate(`/admin/tenants/${t.id}`)}
                        className="text-[#64748B] hover:text-[#0F172A]"
                        data-testid={`view-tenant-${t.id}`}
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="create-tenant-dialog">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-[#0F172A] tracking-tight">Nuevo Tenant</DialogTitle>
          </DialogHeader>
          {error && <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">{error}</div>}
          <form onSubmit={handleCreate} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-widest text-[#64748B] font-semibold">Nombre</Label>
                <Input value={form.name} onChange={e => setForm({...form, name: e.target.value, slug: e.target.value.toLowerCase().replace(/[^a-z0-9]/g, '-')})} required className="bg-white border-[#E2E8F0] text-sm" data-testid="tenant-name-input" />
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-widest text-[#64748B] font-semibold">Slug</Label>
                <Input value={form.slug} onChange={e => setForm({...form, slug: e.target.value})} required className="bg-white border-[#E2E8F0] text-sm" data-testid="tenant-slug-input" />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-widest text-[#64748B] font-semibold">Vertical</Label>
                <Select value={form.vertical} onValueChange={v => setForm({...form, vertical: v, subtype: ''})}>
                  <SelectTrigger className="bg-white border-[#E2E8F0] text-sm" data-testid="tenant-vertical-select">
                    <SelectValue placeholder="Seleccionar" />
                  </SelectTrigger>
                  <SelectContent>
                    {verticals.map(v => <SelectItem key={v.key} value={v.key}>{v.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              {/* 2026-07-24 — tipificación de sub-vertical, solo para consulta/BI futura */}
              {(verticals.find(v => v.key === form.vertical)?.subtypes?.length > 0) && (
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-widest text-[#64748B] font-semibold">Sub-vertical (opcional)</Label>
                  <Select value={form.subtype || '__none__'} onValueChange={v => setForm({...form, subtype: v === '__none__' ? '' : v})}>
                    <SelectTrigger className="bg-white border-[#E2E8F0] text-sm" data-testid="tenant-subtype-select">
                      <SelectValue placeholder="Seleccionar" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">Sin especificar</SelectItem>
                      {verticals.find(v => v.key === form.vertical).subtypes.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-widest text-[#64748B] font-semibold">Plan</Label>
                <Select value={form.plan_id} onValueChange={v => setForm({...form, plan_id: v})}>
                  <SelectTrigger className="bg-white border-[#E2E8F0] text-sm" data-testid="tenant-plan-select">
                    <SelectValue placeholder="Seleccionar plan" />
                  </SelectTrigger>
                  <SelectContent>
                    {catalogPlans.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-widest text-[#64748B] font-semibold">País</Label>
                <Input value={form.country} onChange={e => setForm({...form, country: e.target.value})} className="bg-white border-[#E2E8F0] text-sm" data-testid="tenant-country-input" />
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-widest text-[#64748B] font-semibold">Zona horaria</Label>
                <Select value={form.timezone} onValueChange={v => setForm({...form, timezone: v})}>
                  <SelectTrigger className="bg-white border-[#E2E8F0] text-sm" data-testid="tenant-timezone-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="America/Mexico_City">America/Mexico_City</SelectItem>
                    <SelectItem value="America/Monterrey">America/Monterrey</SelectItem>
                    <SelectItem value="America/Cancun">America/Cancun</SelectItem>
                    <SelectItem value="America/Tijuana">America/Tijuana</SelectItem>
                    <SelectItem value="America/Bogota">America/Bogota</SelectItem>
                    <SelectItem value="America/Lima">America/Lima</SelectItem>
                    <SelectItem value="America/Santiago">America/Santiago</SelectItem>
                    <SelectItem value="America/Buenos_Aires">America/Buenos_Aires</SelectItem>
                    <SelectItem value="America/New_York">America/New_York</SelectItem>
                    <SelectItem value="America/Los_Angeles">America/Los_Angeles</SelectItem>
                    <SelectItem value="Europe/Madrid">Europe/Madrid</SelectItem>
                    <SelectItem value="UTC">UTC</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="border-t border-[#E2E8F0] pt-4 mt-4">
              <p className="text-xs uppercase tracking-widest text-[#64748B] font-semibold mb-3">Owner del Tenant (opcional)</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-xs text-[#64748B]">Nombre</Label>
                  <Input value={form.owner_name} onChange={e => setForm({...form, owner_name: e.target.value})} className="bg-white border-[#E2E8F0] text-sm" data-testid="owner-name-input" />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs text-[#64748B]">Email</Label>
                  <Input type="email" value={form.owner_email} onChange={e => setForm({...form, owner_email: e.target.value})} className="bg-white border-[#E2E8F0] text-sm" data-testid="owner-email-input" />
                </div>
              </div>
              <div className="space-y-2 mt-3">
                <Label className="text-xs text-[#64748B]">Contraseña</Label>
                <Input type="password" value={form.owner_password} onChange={e => setForm({...form, owner_password: e.target.value})} className="bg-white border-[#E2E8F0] text-sm" data-testid="owner-password-input" placeholder="Mínimo 8 caracteres" />
              </div>
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <Button type="button" variant="outline" onClick={() => setShowCreate(false)} className="border-[#E2E8F0] text-[#64748B]" data-testid="cancel-create-tenant">
                Cancelar
              </Button>
              <Button type="submit" disabled={creating || !form.name || !form.vertical} className="bg-[#F72585] text-white hover:bg-[#D81B60]" data-testid="submit-create-tenant">
                {creating ? 'Creando...' : 'Crear Tenant'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
